import React, { useState, useEffect, useCallback } from 'react';
import './UGStudentDashboard.css';
import collegeConfig from '../../config/collegeConfig';
import { FaChevronLeft, FaStethoscope, FaPlus, FaTimes, FaPencilAlt, FaTrash, FaCheckCircle } from 'react-icons/fa';
import Pagination, { ITEMS_PER_PAGE } from './Pagination';

const EXPOSURE_TYPES = ['Hospital-based', 'Community-based', 'Classroom/AV-based'];

const emptyForm = () => ({
  date: new Date().toISOString().slice(0, 10),
  exposureType: 'Hospital-based',
  departmentOrSite: '',
  linkedSubject: '',
  topic: '',
  learningPoints: '',
  reflection: ''
});

const formatDate = d => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

const UGEce = ({ onBack }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalEntry, setModalEntry] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const token = localStorage.getItem('token');
  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/ece/mine`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setSessions(data.sessions || []);
    } catch (err) {
      console.error('Error fetching ECE sessions:', err);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const openNew = () => { setForm(emptyForm()); setModalEntry('new'); };
  const openEdit = (entry) => {
    setForm({
      date: new Date(entry.date).toISOString().slice(0, 10),
      exposureType: entry.exposureType,
      departmentOrSite: entry.departmentOrSite,
      linkedSubject: entry.linkedSubject,
      topic: entry.topic,
      learningPoints: entry.learningPoints || '',
      reflection: entry.reflection || ''
    });
    setModalEntry(entry);
  };

  const submit = async () => {
    if (!form.departmentOrSite.trim() || !form.linkedSubject.trim() || !form.topic.trim()) {
      alert('Please fill in Department/Site, Linked Subject and Topic before saving.');
      return;
    }
    const isEdit = modalEntry && modalEntry !== 'new';
    try {
      setSubmitting(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/ece${isEdit ? `/${modalEntry._id}` : ''}`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.success) {
        setModalEntry(null);
        await fetchSessions();
      } else {
        alert(data.message || 'Failed to save ECE session');
      }
    } catch (err) {
      console.error('Error saving ECE session:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (entry) => {
    if (!window.confirm('Delete this ECE session? This cannot be undone.')) return;
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/ece/${entry._id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (data.success) await fetchSessions();
      else alert(data.message || 'Failed to delete');
    } catch (err) {
      console.error('Error deleting ECE session:', err);
    }
  };

  // Explicit resubmit after a "needs revision" decision — editing the
  // entry alone does not flip its review status back to pending.
  const resubmit = async (entry) => {
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/ece/${entry._id}/resubmit`, { method: 'PUT', headers: authHeaders() });
      const data = await res.json();
      if (data.success) await fetchSessions();
      else alert(data.message || 'Failed to resubmit');
    } catch (err) {
      console.error('Error resubmitting ECE session:', err);
    }
  };

  return (
    <main className="ug-main">
      <button className="ug-back-btn" onClick={onBack}><FaChevronLeft /> Back to Dashboard</button>
      <h2 className="ug-section-title"><FaStethoscope /> Early Clinical Exposure</h2>
      <p className="ug-modal-hint" style={{ margin: '0 0 14px 0' }}>
        NMC's CBME curriculum sends students into clinical departments and community settings from month one of Phase 1, to correlate pre-clinical subjects with real clinical context. Log each ECE session below.
      </p>

      <button className="ug-btn-primary ug-reflection-add-btn" onClick={openNew}>
        <FaPlus /> Add ECE session
      </button>

      {loading ? (
        <div className="ug-due-empty">Loading ECE sessions...</div>
      ) : sessions.length === 0 ? (
        <div className="ug-due-empty">
          No ECE sessions logged yet. Log each clinical/community visit with the pre-clinical subject it correlates to.
        </div>
      ) : (
        <>
        <div className="ug-reflection-list">
          {sessions.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map(entry => (
            <div key={entry._id} className="ug-reflection-card">
              <div className="ug-reflection-card-top">
                <span className="ug-reflection-type-badge ug-reflection-type-weekly">{entry.exposureType}</span>
                <span className="ug-reflection-date">{formatDate(entry.date)}</span>
                {entry.review?.status === 'approved' && <span className="ug-status-badge ug-status-approved"><FaCheckCircle /> Approved</span>}
                {entry.review?.status === 'needs_revision' && <span className="ug-status-badge ug-status-revision">Needs revision</span>}
              </div>
              <h4 className="ug-reflection-period">{entry.topic}</h4>
              <div className="ug-submission-meta" style={{ marginTop: 0 }}>
                <span>Site: {entry.departmentOrSite}</span>
                <span>Linked subject: {entry.linkedSubject}</span>
              </div>

              {entry.learningPoints && (
                <>
                  <div className="ug-reflection-section-label">Learning Points</div>
                  <p className="ug-reflection-text">{entry.learningPoints}</p>
                </>
              )}
              {entry.reflection && (
                <>
                  <div className="ug-reflection-section-label">Reflection</div>
                  <p className="ug-reflection-text">{entry.reflection}</p>
                </>
              )}
              {(entry.review?.feedback || entry.facultyFeedback) && (
                <>
                  <div className="ug-reflection-section-label">Faculty Feedback</div>
                  <p className="ug-reflection-text ug-reflection-faculty-feedback">{entry.review?.feedback || entry.facultyFeedback}</p>
                </>
              )}
              {entry.review?.reviewerName && (
                <p className="ug-reviewer-tag">
                  {entry.review.status === 'approved' ? 'Verified by' : 'Reviewed by'} {entry.review.reviewerName}
                </p>
              )}

              <div className="ug-reflection-card-actions">
                {entry.review?.status !== 'approved' && (
                  <>
                    <button className="ug-reflection-icon-btn" onClick={() => openEdit(entry)} title="Edit"><FaPencilAlt /></button>
                    <button className="ug-reflection-icon-btn ug-reflection-icon-btn-danger" onClick={() => remove(entry)} title="Delete"><FaTrash /></button>
                  </>
                )}
                {entry.review?.status === 'needs_revision' && (
                  <button className="ug-log-btn" onClick={() => resubmit(entry)}>Resubmit for review</button>
                )}
              </div>
            </div>
          ))}
        </div>
        <Pagination currentPage={currentPage} totalCount={sessions.length} onPageChange={setCurrentPage} />
        </>
      )}

      {modalEntry && (
        <div className="ug-modal-overlay" onClick={() => !submitting && setModalEntry(null)}>
          <div className="ug-modal" onClick={e => e.stopPropagation()}>
            <div className="ug-modal-head">
              <h3>{modalEntry === 'new' ? 'Add ECE session' : 'Edit ECE session'}</h3>
              <FaTimes onClick={() => !submitting && setModalEntry(null)} />
            </div>

            <label className="ug-form-label">Date</label>
            <input
              type="date"
              className="ug-form-input"
              value={form.date}
              onChange={e => setForm({ ...form, date: e.target.value })}
            />

            <label className="ug-form-label">Exposure type</label>
            <select
              className="ug-form-input"
              value={form.exposureType}
              onChange={e => setForm({ ...form, exposureType: e.target.value })}
            >
              {EXPOSURE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>

            <label className="ug-form-label">Department / Site</label>
            <input
              type="text"
              className="ug-form-input"
              placeholder="e.g. Medicine OPD, Village PHC"
              value={form.departmentOrSite}
              onChange={e => setForm({ ...form, departmentOrSite: e.target.value })}
            />

            <label className="ug-form-label">Linked pre-clinical subject</label>
            <input
              type="text"
              className="ug-form-input"
              placeholder="e.g. Anatomy, Physiology, Biochemistry"
              value={form.linkedSubject}
              onChange={e => setForm({ ...form, linkedSubject: e.target.value })}
            />

            <label className="ug-form-label">Topic</label>
            <input
              type="text"
              className="ug-form-input"
              placeholder="e.g. Correlating brachial plexus anatomy with clinical exam"
              value={form.topic}
              onChange={e => setForm({ ...form, topic: e.target.value })}
            />

            <label className="ug-form-label">Learning points (optional)</label>
            <textarea
              className="ug-form-input ug-form-textarea"
              value={form.learningPoints}
              onChange={e => setForm({ ...form, learningPoints: e.target.value })}
            />

            <label className="ug-form-label">Reflection (optional)</label>
            <textarea
              className="ug-form-input ug-form-textarea"
              value={form.reflection}
              onChange={e => setForm({ ...form, reflection: e.target.value })}
            />

            <div className="ug-modal-actions">
              <button className="ug-btn-secondary" onClick={() => setModalEntry(null)} disabled={submitting}>Cancel</button>
              <button className="ug-btn-primary" onClick={submit} disabled={submitting}>
                {submitting ? 'Saving...' : 'Save ECE session'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default UGEce;
